import { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store/reducers";
import { AppDispatch } from "../store/store";
import {
  setPageFilter,
  clearPageFilter,
} from "../store/modules/pageFilters/pageFiltersSlice";

/**
 * Custom hook for managing page-specific co-agent filters with Redux state.
 * Automatically clears page filter when global filter becomes active.
 * Page filters are NOT persisted (session-only), while global filters are persisted.
 *
 * @param pageKey - Unique identifier for the page (e.g., "businessZones", "financialTrends")
 * @param globalFilterIds - Global co-agent filter IDs from Redux state
 * @returns [effectiveFilterIds, setPageFilterIds, isGlobalFilterActive]
 */
export const usePageCoAgentFilter = (
  pageKey:
    | "businessZones"
    | "financialTrends"
    | "analyticsReports"
    | "businessPlanning"
    | "repeatsAndReferrers",
  globalFilterIds: number[],
): [number[], (ids: number[]) => void, boolean] => {
  const dispatch = useDispatch<AppDispatch>();
  const isGlobalFilterActive = globalFilterIds.length > 0;

  // Get page filter from Redux state
  const pageFilterIds = useSelector(
    (state: RootState) => state.pageFilters[pageKey],
  );

  // Clear page filter when global filter becomes active
  useEffect(() => {
    if (isGlobalFilterActive && pageFilterIds.length > 0) {
      dispatch(clearPageFilter(pageKey));
    }
  }, [isGlobalFilterActive, pageFilterIds.length, pageKey, dispatch]);

  // Setter that updates Redux state (only when no global filter)
  const setPageFilterIds = useCallback(
    (ids: number[]) => {
      if (isGlobalFilterActive) {
        // Don't allow setting page filter when global is active
        return;
      }
      dispatch(setPageFilter({ pageKey, ids }));
    },
    [isGlobalFilterActive, pageKey, dispatch],
  );

  // Determine effective filter IDs (global takes priority)
  const effectiveFilterIds = isGlobalFilterActive
    ? globalFilterIds
    : pageFilterIds;

  return [effectiveFilterIds, setPageFilterIds, isGlobalFilterActive];
};
